"use client";

import { useState } from "react";
import { useLocaleRouter } from "@/lib/hooks/use-locale-router";
import { useTranslations, useLocale } from "next-intl";
import { ListMagnifyingGlass, X } from "@phosphor-icons/react";
import SearchAutocomplete from "@/components/ui/SearchAutocomplete";
import ProvinceFilter from "@/components/ui/ProvinceFilter";
import type { Locale } from "@/lib/locale";
import { getCategoryLabel, type DirectoryCategory } from "@/lib/categories";
import { canonicalLanguageLabel, langLabelStandalone, LANG_LABEL_TO_CODE } from "@/lib/lang-utils";
import type { LocationSlug } from "@/lib/provinces";
import { SEARCH_COMPOSER_OPEN_EVENT } from "@/lib/events";
import type { SuggestionCounts, SpecialtySuggestion } from "@/lib/queries/professionals";

interface SearchFiltersProps {
  q: string;
  category: string;
  location: string;
  language: string;
  sort: string;
  categories: string[];
  locations: string[];
  languages: string[];
  hasFilters: boolean;
  /** Suggestion counts for autocomplete in the desktop sidebar */
  counts?: SuggestionCounts;
  /** Specialty suggestions for autocomplete */
  specialties?: SpecialtySuggestion[];
}

function FilterRow({
  name,
  value,
  current,
  label,
  onChange,
}: {
  name: string;
  value: string;
  current: string;
  label: string;
  onChange: (val: string) => void;
}) {
  const active = current === value;
  return (
    <li>
      <label className="flex cursor-pointer items-center gap-13 text-sm text-gray-600 hover:text-navy select-none">
        <input
          type="radio"
          name={name}
          value={value}
          checked={active}
          onChange={() => onChange(value)}
          className="sr-only"
        />
        <span
          className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border transition-colors ${
            active ? "border-navy" : "border-navy/25 bg-white"
          }`}
          aria-hidden="true"
        >
          {active && <span className="h-[7px] w-[7px] rounded-full bg-navy" />}
        </span>
        {label}
      </label>
    </li>
  );
}

/**
 * /search filter controls.
 *
 * - Desktop (md+): live-update sidebar with autocomplete-search input.
 *   Each radio click navigates immediately, results refresh in the
 *   adjacent results pane.
 * - Mobile (below md): a thin toolbar with Sort + a "Filters" trigger.
 *   The trigger dispatches the global open-composer event so the
 *   universal SearchComposer (mounted in the marketing layout) opens
 *   in its place. The composer always operates in buffered mode.
 */
export default function SearchFilters(props: SearchFiltersProps) {
  const { q, category, location, language, sort, categories, languages, hasFilters, counts, specialties } = props;
  const router = useLocaleRouter();
  const t = useTranslations("search");
  const locale = useLocale() as Locale;

  const activeCount = [category, location, language, q].filter(Boolean).length;

  // `clearNonce` bumps on every Clear-all click. Used as `key` on
  // ProvinceFilter so the "Enter location" search input (internal local
  // state, unrelated to URL) remounts and clears.
  //
  // `SearchAutocomplete` uses `key={q}` instead — its `defaultValue=q` is
  // uncontrolled and doesn't react to subsequent prop changes, so remounts
  // need to fire when q itself changes (e.g. q="lawyer" → q=""). Bumping a
  // nonce synchronously on click doesn't work for it: the click handler
  // runs before Next.js navigation has propagated the new q, so a fresh
  // mount would still get defaultValue="lawyer".
  //
  // We don't tie clearNonce to `hasFilters` directly either — picking "All
  // Categories" mid-typing also flips `hasFilters` to false but shouldn't
  // clobber the user's draft.
  const [clearNonce, setClearNonce] = useState(0);

  function clearAll() {
    setClearNonce((n) => n + 1);
    router.push("/search");
  }

  function navigate(overrides: Partial<Record<string, string>>) {
    const params = new URLSearchParams();
    const values: Record<string, string> = { q, category, location, language, sort, ...overrides };
    for (const [k, v] of Object.entries(values)) {
      if (v) params.set(k, v);
    }
    router.push(`/search?${params.toString()}`);
  }

  function openComposer() {
    // Cancelable so the dispatcher can detect whether the composer is
    // mounted; on /search it always is.
    window.dispatchEvent(new CustomEvent(SEARCH_COMPOSER_OPEN_EVENT, { cancelable: true }));
  }

  return (
    <>
      {/* ── Desktop sidebar (md+) ─────────────────────────────────── */}
      <aside
        aria-label={t("filterAside")}
        className="hidden rounded-card border border-card-line bg-white p-21 shadow-e1 md:block md:self-start"
      >
        <div className="space-y-34">
          {/* Search with autocomplete */}
          <div>
            <label
              htmlFor="sidebar-search"
              className="mb-8 block text-[11px] font-semibold uppercase tracking-widest text-navy"
            >
              {t("searchLabel")}
            </label>
            <SearchAutocomplete
              key={q}
              id="sidebar-search"
              counts={counts}
              specialties={specialties}
              defaultValue={q}
              placeholder={t("searchPlaceholder")}
              dropdownMinWidth={320}
              onSelect={({ intent, href, query }) => {
                if (intent === "suggestion" && href) {
                  router.push(href);
                } else {
                  navigate({ q: query });
                }
              }}
            />
          </div>

          {/* Category */}
          <fieldset>
            <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
              {t("categoryLegend")}
            </legend>
            <ul role="list" className="space-y-8">
              <FilterRow name="category" value="" current={category} label={t("allCategories")} onChange={(v) => navigate({ category: v, q: "" })} />
              {categories.map((cat) => (
                <FilterRow key={cat} name="category" value={cat} current={category} label={getCategoryLabel(cat as DirectoryCategory, locale)} onChange={(v) => navigate({ category: v, q: "" })} />
              ))}
            </ul>
          </fieldset>

          {/* Location — searchable across all 34 post-2025-reform
              provinces, with ward-name shortcuts (e.g. "Hoàn Kiếm" →
              Hà Nội). Live URL update on each pick. */}
          <fieldset>
            <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
              {t("provinceLegend")}
            </legend>
            <ProvinceFilter
              key={clearNonce}
              value={location as LocationSlug}
              onChange={(v) => navigate({ location: v })}
              groupLabel={t("filterByProvince")}
            />
          </fieldset>

          {/* Language */}
          <fieldset>
            <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
              {t("languageLegend")}
            </legend>
            <ul role="list" className="space-y-8">
              <FilterRow name="language" value="" current={canonicalLanguageLabel(language)} label={t("allLanguages")} onChange={(v) => navigate({ language: v })} />
              {languages.map((lang) => {
                // Filter value stays the English name (the DB filter value); only
                // the display label localizes via langLabelStandalone(code, locale).
                const code = LANG_LABEL_TO_CODE[lang.toLowerCase()];
                return (
                  <FilterRow key={lang} name="language" value={lang} current={canonicalLanguageLabel(language)} label={code ? langLabelStandalone(code, locale) : lang} onChange={(v) => navigate({ language: v })} />
                );
              })}
            </ul>
          </fieldset>

          {/* Clear all — button (not Link) so we can bump `clearNonce` and
              force the two children with internal text state to remount
              alongside the URL reset. */}
          {hasFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="mt-[42px] inline-flex w-full items-center justify-center gap-5 rounded-control border border-navy/15 px-13 py-[12px] text-sm font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:border-navy/40 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <X className="h-[11px] w-[11px]" weight="bold" aria-hidden="true" />
              {t("clearAllFilters")}
            </button>
          )}
        </div>
      </aside>

      {/* ── Mobile toolbar (below md) ──────────────────────────────────
           Right-aligned: [Filter Professionals btn] [Clear if active].
           The Filter button opens the SearchComposer drawer and is shown on
           every size below md. Since the bottom bar was retired (2026-09-05)
           this is the drawer's ONLY entry point — see the composer note in
           CLAUDE.md. */}
      <div className="flex items-center justify-end gap-8 py-13 md:hidden">
        <button
          type="button"
          onClick={openComposer}
          // `hover:border-navy` (full navy) is intentionally bolder
          // than the `/40` tint on the toolbar's Clear link — Filter
          // Professionals is the toolbar's primary action and earns a
          // stronger affordance.
          className="inline-flex shrink-0 items-center gap-8 rounded-control border border-navy/15 bg-white px-13 py-8 text-[11px] font-semibold uppercase tracking-widest text-navy shadow-e1 transition-[box-shadow,border-color] hover:border-navy/30 hover:shadow-e2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
          aria-haspopup="dialog"
          aria-label={
            activeCount > 0
              ? t("filterProfessionalsActive", { count: activeCount })
              : t("filterProfessionals")
          }
        >
          <ListMagnifyingGlass className="h-[14px] w-[14px]" weight="regular" aria-hidden="true" />
          {t("filterProfessionals")}
          {activeCount > 0 && (
            <span
              aria-hidden="true"
              className="inline-flex h-[16px] w-[16px] items-center justify-center rounded-full bg-navy text-[9px] font-bold text-white"
            >
              {activeCount}
            </span>
          )}
        </button>

        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex shrink-0 items-center gap-5 rounded-control border border-navy/15 px-13 py-8 text-[11px] font-semibold uppercase tracking-widest text-gray-600 transition-colors hover:border-navy/40 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
          >
            <X className="h-[11px] w-[11px]" weight="bold" aria-hidden="true" />
            {t("clear")}
          </button>
        )}
      </div>
    </>
  );
}
